import React from 'react';
import { 
  Box, Typography, Button, TextField, 
  Grid, Card, CardContent, IconButton, Autocomplete, Alert
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowBack } from '@mui/icons-material';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { 
  createExtraTypeConfig, 
  updateExtraTypeConfig, 
  fetchExtraTypeConfigById,
  fetchExtraTypeConfigList,
  fetchExtraTypeList 
} from '../../Domain/Services/ExtraTypeConfigService';
import { getDelegationList } from 'Delegation/Domain/Services/GetDelegationListService';
import { PRIVATE_ADMIN_EXTRA_CONFIG } from '../Constants/PathsConstant';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';

function ExtraTypeConfigFormView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin, checkPermission } = useAuthContext();
  const hasAccess = isAdmin() || checkPermission('EXTRAS');
  const isEdit = id && id !== 'new';

  const todayString = React.useMemo(() => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  const [loading, setLoading] = React.useState(false);
  const [fetchingData, setFetchingData] = React.useState(true);
  const [extraTypes, setExtraTypes] = React.useState([]);
  const [delegations, setDelegations] = React.useState([]);

  const [formData, setFormData] = React.useState({
    startDate: '',
    endDate: '',
    amount: '',
    extraTypeId: '',
    delegationId: ''
  });

  React.useEffect(() => {
    if (!hasAccess) {
      setFetchingData(false);
      return;
    }
    const loadInitialData = async () => {
      setFetchingData(true);
      try {
        const [types, dels] = await Promise.all([
          fetchExtraTypeList(),
          getDelegationList()
        ]);
        setExtraTypes(types);
        setDelegations(dels.records || []);

        if (isEdit) {
          const currentConfig = await fetchExtraTypeConfigById(id);
          if (currentConfig) {
            const fetchedEndDate = currentConfig.endDate ? currentConfig.endDate.split(' ')[0] : '';
            setFormData({
              startDate: currentConfig.startDate ? currentConfig.startDate.split(' ')[0] : '',
              endDate: fetchedEndDate === '9999-12-31' ? '' : fetchedEndDate,
              amount: currentConfig.amount,
              extraTypeId: currentConfig.extraTypeId,
              delegationId: currentConfig.delegationId
            });
          }
        }
      } catch (error) {
        toast.error("Error al cargar los datos");
      } finally {
        setFetchingData(false);
      }
    };

    loadInitialData();
  }, [id, isEdit, hasAccess]);

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', borderRadius: 2 }}>
          No tienes permisos para ver o modificar la configuración de extras.
        </Alert>
      </Box>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.extraTypeId || !formData.delegationId || !formData.startDate) {
      toast.warning("Por favor, completa todos los campos obligatorios");
      return;
    }
    if (!isEdit && formData.startDate < todayString) {
      toast.warning("La fecha de inicio no puede ser una fecha pasada");
      return;
    }
    setLoading(true);
    try {
      let finalEndDate = formData.endDate || '9999-12-31';

      // Buscar configuraciones existentes para este tipo y delegación (excluyendo la actual si estamos editando)
      const allConfigs = await fetchExtraTypeConfigList();
      const existingConfigs = allConfigs
        .filter(c => c.extraTypeId === formData.extraTypeId && c.delegationId === formData.delegationId && String(c.id) !== String(id))
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate)); // De más reciente a más antigua

      const latestConfig = existingConfigs[0];

      // Si la fecha de inicio que estamos guardando es posterior a la más reciente existente
      if (latestConfig && new Date(formData.startDate) > new Date(latestConfig.startDate)) {
        // Calculamos el día anterior a la nueva fecha de inicio usando fecha local
        const startDateParts = formData.startDate.split('-').map(Number);
        const prevDate = new Date(startDateParts[0], startDateParts[1] - 1, startDateParts[2]);
        prevDate.setDate(prevDate.getDate() - 1);
        
        const y = prevDate.getFullYear();
        const m = String(prevDate.getMonth() + 1).padStart(2, '0');
        const d = String(prevDate.getDate()).padStart(2, '0');
        const formattedPrevDate = `${y}-${m}-${d}`;

        // Cerramos la configuración anterior
        await updateExtraTypeConfig(latestConfig.id, {
          ...latestConfig,
          id: parseInt(latestConfig.id, 10),
          endDate: formattedPrevDate + ' 00:00:00'
        });

        // La que estamos guardando ahora pasa a ser la más actual (fin infinito)
        finalEndDate = '9999-12-31';
      }

      const dataToSubmit = {
        ...formData,
        amount: parseFloat(formData.amount),
        startDate: formData.startDate + ' 00:00:00',
        endDate: finalEndDate + (finalEndDate.includes(':') ? '' : ' 00:00:00')
      };

      if (isEdit) {
        dataToSubmit.id = parseInt(id, 10);
        await updateExtraTypeConfig(id, dataToSubmit);
        toast.success(t('extra.afterSalesExtra.config.messages.successUpdate'));
      } else {
        await createExtraTypeConfig(dataToSubmit);
        toast.success(t('extra.afterSalesExtra.config.messages.successCreate'));
      }
      navigate(PRIVATE_ADMIN_EXTRA_CONFIG);
    } catch (error) {
      toast.error("Error al guardar la configuración");
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) return <DialogLoader open={true} />;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigate(PRIVATE_ADMIN_EXTRA_CONFIG)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          {isEdit ? t('extra.afterSalesExtra.config.edit') : t('extra.afterSalesExtra.config.add')}
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 1200, mx: 'auto', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={extraTypes}
                  getOptionLabel={(option) => option.name || ''}
                  value={extraTypes.find(t => t.id === formData.extraTypeId) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, extraTypeId: newValue ? newValue.id : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('extra.afterSalesExtra.config.fields.extraType')} 
                      required 
                      placeholder="Busca o selecciona un tipo de extra..."
                    />
                  )}
                  ListboxProps={{
                    sx: {
                      '& .MuiAutocomplete-option': {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={delegations}
                  getOptionLabel={(option) => option.name || ''}
                  value={delegations.find(d => d.id === formData.delegationId) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, delegationId: newValue ? newValue.id : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('extra.afterSalesExtra.config.fields.delegation')} 
                      required 
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('extra.afterSalesExtra.config.fields.amount')}
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  inputProps={{ step: "0.01" }}
                />
              </Grid>

              <Grid item xs={12} md={isEdit ? 6 : 12}>
                <TextField
                  fullWidth
                  required
                  label={t('extra.afterSalesExtra.config.fields.startDate')}
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: !isEdit ? todayString : (formData.startDate < todayString ? formData.startDate : todayString)
                  }}
                />
              </Grid>

              {isEdit && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('extra.afterSalesExtra.config.fields.endDate')}
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}


              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  sx={{ borderRadius: 2, px: 4, py: 1 }}
                >
                  Guardar
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      <DialogLoader open={loading} />
    </Box>
  );
}

export default ExtraTypeConfigFormView;
